import { BCMSStateManagerCRUD, Branding, BrandingImage, CCTM, CCTM_demo, CCTM_item, CCTM_link, CCTM_route, Link, useBCMSModal, useCCTM, useModel, useRoute, useStateManager, useWorkflow } from "@bcm-dev/bcms-vite";
import React, { useEffect } from "react";

import { LeftColumnContainer, PageContainer, PageFooterView, RightColumnContainer } from 'components/shared';
import FormContainer, { FormRowView } from 'components/shared/containers/FormContainer';
import { CenterContentContainer, LayoutContainer } from 'components/shared/containers/PageLayoutContainer';
import { PrimaryButton } from 'components/ui/UIButton';
import UIPasswordInput from 'components/ui/UIPasswordInput';
import UITextInput from 'components/ui/UITextInput';

import { ImageAsset } from "res";
import './styles.css';

/**
 * @name LoginPage
 * @param {OBJECT} data - Data passed to the page from either a workflow or capture
 * @param {OBJECT} ui - UI passed to the page from either a workflow or capture
 * @return {React.Component}
 * @description The main component for the login page
 */
export default function LoginPage(props) {
    // Set the data and ui variables with the default values and whats passed
    // through the properties

    // "useWorkflow" is currently only set through the "ChatWorkflow" 
    // component since this page is included in that workflow.
    const workflow = useWorkflow();
    const hasWorkflow = workflow ? true : false;
    const { branding } = useModel() ?? {};
    const { nextStep } = useWorkflow() ?? {};
    const data = { ...getDefaultPageData(hasWorkflow, nextStep, branding), ...props.data ?? {} };
    const ui = { ...getDefaultPageUI(), ...props.ui ?? {} };

    // The state that will be passed to the state manager
    const state = { data, ui };
    // :: React.state, React.setState -> OBJECT
    // Function that returns the controller for the state manager
    const controller = (state, setState) => ({
        data: state.data, // Set the data property in the controller
        ui: state.ui, // Set the ui property in the controller 

        // onChange :: STRING, * -> VOID
        // Update the data based on the passed prop and value parameters
        onChange: prop => value => {
            BCMSStateManagerCRUD.init(state)
                .update([ "data", prop ], value)
                .submit(setState);
        }
    });

    return (
        <PageContainer className="login-page" state={ state } controller={ controller }>
            <LoginContent />
            <PageFooterView />
        </PageContainer>
    )
}

/**
 * @name getDefaultPageData
 * @returns {OBJECT}
 * @description Hook to return the default data that the pages needs
 */
export const getDefaultPageData = (hasWorkflow, nextStep, branding) => ({
    userID: "DemoUser",
    password: "123456789",
    branding,
    nextStep,
    hasWorkflow
});

/**
 * @name getDefaultPageUI
 * @returns {OBJECT}
 * @description Hook to return the default ui that the page needs
 */
export const getDefaultPageUI = () => ({
    modal: {
      id: undefined,
      data: undefined
    }
});

/**
 * @name LoginContent
 * @returns {React.Component}
 * @description Content component for the login page
 */
function LoginContent(props) {
    // Get the data, ui and onChange properties from the state manager
    const { data, ui, onChange } = useStateManager();
    const hasWorkflow = data.hasWorkflow;
    const { open } = useBCMSModal();
    const navigate = useRoute();

    // Once the page is loaded, if the modal property of the ui has been set
    // then open the modal
    useEffect(() => {
      if(ui.modal.id !== undefined){ open(ui.modal.id, ui.modal.data); }
    }, [ props ]);

    // onLoginClick :: EVENT -> VOID
    // This function would typically be in the "controller", but "useBCMSModal" can't be 
    // used in the "LoginPage" component since that component hasn't been initialized yet.
    const onLoginClick = evt => {
        // If this page is in a workflow, then go to the next step. 
        // Currently the only workflow component using this page is "ChatWorkflow".
        if(hasWorkflow) {
            data.nextStep();
        } else if(data.branding.options.first_time_login_abi22 === "true") {
            open("FirstTimeLoginModal");
        } else {
            navigate("/accountSummary");
        }
    };
    const xml =  useCCTM(),
    bottomLeftMenu = <CCTM nav-id={"loginBottomLeftNav"} xml={xml} self={LeftColumnContainer}>
                                        
                <CCTM_route component={LoginNavigationItem}/>                  
                <CCTM_link component={LoginNavigationItem}/> 
                <CCTM_item component={LoginNavigationItem}/>                                             
                <CCTM_demo component={LoginNavigationItem} /> 
                          
            </CCTM>,

bottomRightMenu = <CCTM nav-id={"loginBottomRightNav"} xml={xml} self={RightColumnContainer}>
                                    
            <CCTM_route component={LoginNavigationItem}/>                  
            <CCTM_link component={LoginNavigationItem}/> 
            <CCTM_item component={LoginNavigationItem}/>                                             
            <CCTM_demo component={LoginNavigationItem} /> 
                      
        </CCTM>,
LoginWigetMenu = <CCTM nav-id={"loginWidgetLinks"} xml={xml} self={ LoginNavWidgetItemWrapper}>
                                    
<CCTM_route component={LoginNavWidgetItem}/>                  
<CCTM_link component={LoginNavWidgetItem}/> 
<CCTM_item component={LoginNavWidgetItem}/>                                             
<CCTM_demo component={LoginNavWidgetItem} /> 
          
</CCTM>;
    return (
        <div className="login-page-container flex-width flex-container flex-vertical">
            <CenterContentContainer>
                <div className="fi-logo-container">
                    {/* FI Logo here */}
                    <BrandingImage id="login_logo_abi22" className="login-header-logo"/>
                </div>
                {/* FDIC Logo here */}
                <Branding id="fdic_abi22" value="Colored (White BG)">
                    <div className="fi-logo-container">
                            <ImageAsset id="FDICDigitalSign" className="login-fdic-header-logo" />
                    </div>
                </Branding>
                <Branding id="fdic_abi22" value="White (Dark BG)">
                    <div className="fi-logo-container">
                        <ImageAsset id="FDICDigitalSignWhite" className="login-fdic-header-logo" />
                    </div>
                </Branding>
                <FormContainer>
                    <FormRowView text="User ID">
                        <UITextInput readOnly="true" data-point="Login_UserID_Field" value={ data.userID } onChange={ onChange("userID") } />
                    </FormRowView>
                    
                    <FormRowView text="Password">
                        <UIPasswordInput readOnly="true" data-point="Login_Password_Field" value={ data.password } onChange={ onChange("password") } />
                    </FormRowView>
                    
                    <FormRowView>
                        <PrimaryButton onClick={ onLoginClick } text="Log In" data-point="Login_Submit" />
                    </FormRowView>
                    
                    {LoginWigetMenu}
                </FormContainer>
              
                <LayoutContainer>
                {bottomLeftMenu}
                {bottomRightMenu}
                   
                </LayoutContainer>
            </CenterContentContainer>
        </div>
    );
}

/**
 * @function LoginNavigationItem
 * @param {STRING} text - Allows us to set the text of the navigation item.
 * @param {STRING} to - Sets the route of the NavLink.
 * @param {STRING} icon - Sets the icon for the navigation item.
 * @returns {React.Component}
 * @exported false
 * @description This is the individual login navigation item component. It includes text and an icon.
 * @example
 * <LoginNavigationItem text="Location" icon="LocationPinIcon" to="/LocationPage" />
 */
function LoginNavigationItem(props) {

    const text = props.label ?? "[text]";
    const to = props.to ?? "";
    const icon = props.asset_name ?? false;
    const className = props["custom-class"] ?? "";
    const dataPoint = props.dataPoint ? { "data-point": props.dataPoint } : {};
  
    return<>
    {icon && <SVGAsset id={icon} />}
       <Link to={to} className={className} {...dataPoint}>{text}</Link> 
    </>
    
  } 
  function LoginNavWidgetItemWrapper(props){
    return <>
        {props.children}
    </>
  }
  function LoginNavWidgetItem(props) {

    const text = props.label ?? "[text]";
    let to = props.to ?? "";
    const icon = props.asset_name ?? false;
    const dataPoint = props.dataPoint ? { "data-point": props.dataPoint } : {};
    const className = props["custom-class"]? { "className":  props["custom-class"] } : {};
    let onClick = ()=>{};
    if(props.onClick){
        to = "";
        onClick= props.onClick;
    }
    return <FormRowView className="text-center">             
                   <Link to={to} {...dataPoint} onClick={onClick} {...className}>{text}</Link>            
            </FormRowView>
  } 
       
  